import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import "../../styles/practice/practice.css";

const WORDS_LIST = [
	"Frequently", "Numerous", "Superiority", "Hierarchy", "Basis", "Incompatible",
	"Powered", "Urgency", "Sleek", "Hydrogen", "Mysterious", "Sources", "Church",
	"Gaps", "Industrial", "Distinctive", "Matchstick", "Portraits", "Sustainable",
	"Chemistry", "Devices", "Interaction", "Perceptions", "Renewable", "Transitioning",
	"Planet", "Deforestation", "Warmer", "Bored", "Five", "Twenty-six", "Irrelevant",
	"Phenomenon", "Styles", "Farms", "Box", "Tremendous", "Associate", "Technology",
	"Unique", "Continent", "Together", "Conduct", "Determination", "Passions",
	"Patience", "Mobile", "Daughter", "Journalist", "Agency", "Experience", "Scary",
	"Electronics", "Serious", "Negative", "Consume", "Recommended", "Diagnosed",
	"Offensive", "Statistics", "Migration", "Transcontinental", "Plantations",
	"Insistences", "Sojourners", "Labored", "Board", "Recruited", "Insistence",
	"Demography", "Slightly", "Elimination", "Refugee", "Subsequent", "Bachelor",
	"Domestic", "Risk", "Math", "Club", "Active", "Completely", "Interesting",
	"Statistics", "Mathematics", "Subsides", "Interest", "Downtown", "Pollution",
	"Reservation", "Common", "Correct", "Replacement", "Shallow", "Busy", "Assumed",
	"Consumption", "Safe", "Make", "Change", "Color", "Instructor", "Apply", "Problem",
	"Corporation", "Cultures", "Energy", "Distract", "Climate", "Simple", "Complex",
	"Steadily", "Job", "Personality", "Reality", "Cool", "Profound", "Restricted",
	"City", "Flow", "Sugar", "Segment", "Traffic", "Assist", "Study", "Team", "Ability",
	"Average", "Infrastructure", "Everywhere", "Depth", "Nurse", "Aid", "Floor", "Wage",
	"Menu", "Sociable", "Interested", "Originate", "Landscape", "Cast", "Employees",
	"Certain", "Salary", "Favorite", "Incorporate", "Biology", "Allowed", "Career",
	"Eliminate", "Matter", "Care", "Human", "Respond", "Easy", "Experimental",
	"Professional", "Same", "Hours", "Underground", "Blood", "Artist", "Fresh", "Bigger",
	"Sector", "Systems", "Boost", "Contribution", "Start", "Contact", "Throughout",
	"Traditional", "Requiring", "Jobs", "Regular", "Disk", "Bring", "Environment",
	"Ready", "Inspired", "Undergraduate", "Constructing", "Remarkable", "Failure"
];

function Practice() {
	// States for practice flow
	const [stage, setStage] = useState("intro"); // intro, practicing, results
	const [nickname, setNickname] = useState("");
	const [currentWordIndex, setCurrentWordIndex] = useState(0);
	const [userInput, setUserInput] = useState("");
	const [showResult, setShowResult] = useState(false);
	const [isCorrect, setIsCorrect] = useState(false);
	const [results, setResults] = useState([]); // Array of { word, userAnswer, isCorrect }
	const [isSpeaking, setIsSpeaking] = useState(false);

	const inputRef = useRef(null);
	const audioRef = useRef(null);

	// Get the audio file path for a word
	const getAudioPath = (word) => {
		const filename = word.toLowerCase().replace(/[^a-z0-9-]/g, '_');
		return `/audio/words/${filename}.mp3`;
	};

	// Text-to-speech function using pre-generated MP3 files
	const speakWord = (word) => {
		// Stop any currently playing audio
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}

		setIsSpeaking(true);

		const audio = new Audio(getAudioPath(word));
		audioRef.current = audio;

		audio.onended = () => setIsSpeaking(false);
		audio.onerror = () => {
			// Fallback to SpeechSynthesis if MP3 fails to load
			console.warn(`Audio file not found for "${word}", falling back to SpeechSynthesis.`);
			if ('speechSynthesis' in window) {
				// Cancel any ongoing speech
				window.speechSynthesis.cancel();
				const utterance = new SpeechSynthesisUtterance(word);
				utterance.lang = 'en-US';
				utterance.rate = 0.8; // Slightly slower for clarity
				utterance.pitch = 1;
				utterance.onend = () => setIsSpeaking(false);
				utterance.onerror = () => setIsSpeaking(false);
				window.speechSynthesis.speak(utterance);
			} else {
				setIsSpeaking(false);
				alert("Sorry, audio playback is not available.");
			}
		};

		audio.play().catch(() => {
			setIsSpeaking(false);
		});
	};

	// Start practice
	const handleStartPractice = () => {
		setStage("practicing");
		setCurrentWordIndex(0);
		setResults([]);
	};

	// Check the user's answer
	const handleCheckAnswer = () => {
		if (userInput.trim() === "") return;

		const currentWord = WORDS_LIST[currentWordIndex];
		const correct = userInput.trim().toLowerCase() === currentWord.toLowerCase();

		setIsCorrect(correct);
		setShowResult(true);

		// Store result
		setResults(prev => [...prev, {
			word: currentWord,
			userAnswer: userInput.trim(),
			isCorrect: correct
		}]);
	};

	// Re-practice same word
	const handleRepractice = () => {
		// Remove the last result for this word
		setResults(prev => prev.slice(0, -1));
		setUserInput("");
		setShowResult(false);
		if (inputRef.current) {
			inputRef.current.focus();
		}
	};

	// Go to next word
	const handleNextWord = () => {
		if (currentWordIndex < WORDS_LIST.length - 1) {
			const nextIndex = currentWordIndex + 1;
			setCurrentWordIndex(nextIndex);
			setUserInput("");
			setShowResult(false);
			if (inputRef.current) {
				inputRef.current.focus();
			}
		} else {
			// Practice complete
			setStage("results");
		}
	};

	// Handle Enter key press on input
	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			if (userInput.trim() === "") {
				// No text entered, play the word
				speakWord(WORDS_LIST[currentWordIndex]);
			} else {
				// Text entered, check the answer
				handleCheckAnswer();
			}
		}
	};

	// Restart practice
	const handleRestart = () => {
		setStage("intro");
		setNickname("");
		setCurrentWordIndex(0);
		setUserInput("");
		setShowResult(false);
		setResults([]);
	};

	// Calculate results
	const correctCount = results.filter(r => r.isCorrect).length;
	const incorrectCount = results.filter(r => !r.isCorrect).length;
	const percentage = results.length > 0 ? Math.round((correctCount / results.length) * 100) : 0;

	// Focus input when practicing starts
	useEffect(() => {
		if (stage === "practicing" && inputRef.current) {
			inputRef.current.focus();
		}
	}, [stage]);

	// Handle Enter key globally when result is showing (input is disabled)
	useEffect(() => {
		const handleGlobalKeyPress = (e) => {
			if (e.key === "Enter" && stage === "practicing" && showResult) {
				e.preventDefault();
				e.stopPropagation();
				handleNextWord();
			}
		};

		window.addEventListener("keydown", handleGlobalKeyPress, true);
		return () => window.removeEventListener("keydown", handleGlobalKeyPress, true);
	}, [stage, showResult, currentWordIndex]);

	return (
		<section className="practice-page">
			<div className="lamar-container">
				<div className="path">
					<Link to="/">
						<i className="fas fa-home"></i>
					</Link>
					<i className="fas fa-angle-right"></i>
					<span>Spelling Practice</span>
				</div>

				{/* Intro Stage */}
				{stage === "intro" && (
					<div className="practice-intro">
						<h1>🎯 Spelling Practice</h1>
						<p className="intro-description">
							Test your spelling skills! Listen to the word and type the correct spelling.
							There are <strong>{WORDS_LIST.length} words</strong> to practice.
						</p>

						<div className="nickname-section">
							<label htmlFor="nickname">Enter your name or nickname (optional):</label>
							<input
								type="text"
								id="nickname"
								value={nickname}
								onChange={(e) => setNickname(e.target.value)}
								onKeyPress={(e) => e.key === "Enter" && handleStartPractice()}
								placeholder="Your name..."
								maxLength={30}
							/>
						</div>

						<button className="start-btn" onClick={handleStartPractice}>
							<i className="fas fa-play"></i> Start Practice
						</button>
					</div>
				)}

				{/* Practicing Stage */}
				{stage === "practicing" && (
					<div className="practice-main">
						<div className="practice-header">
							{nickname && <p className="welcome-text">Good luck, <strong>{nickname}</strong>!</p>}
							<div className="progress-info">
								<span>Word {currentWordIndex + 1} of {WORDS_LIST.length}</span>
								<div className="progress-bar">
									<div
										className="progress-fill"
										style={{ width: `${((currentWordIndex + 1) / WORDS_LIST.length) * 100}%` }}
									></div>
								</div>
							</div>
						</div>

						<div className="word-practice-area">
							<div className="listen-section">
								<p>🔊 Listen to the word and type its spelling:</p>
								<button
									className={`play-sound-btn ${isSpeaking ? 'speaking' : ''}`}
									onClick={() => speakWord(WORDS_LIST[currentWordIndex])}
									disabled={isSpeaking}
								>
									<i className="fas fa-volume-up"></i>
									{isSpeaking ? ' Playing...' : ' Play Word'}
								</button>
							</div>

							<div className="input-section">
								<input
									ref={inputRef}
									type="text"
									value={userInput}
									onChange={(e) => setUserInput(e.target.value)}
									onKeyPress={handleKeyPress}
									placeholder="Type the spelling here..."
									disabled={showResult}
									autoComplete="off"
									autoCapitalize="off"
									spellCheck="false"
								/>

								{!showResult && (
									<button
										className="check-btn"
										onClick={handleCheckAnswer}
										disabled={userInput.trim() === ""}
									>
										<i className="fas fa-check"></i> Check
									</button>
								)}
							</div>

							{/* Result Feedback */}
							{showResult && (
								<div className={`result-feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
									{isCorrect ? (
										<>
											<i className="fas fa-check-circle"></i>
											<span>Correct! Well done! 🎉</span>
										</>
									) : (
										<>
											<i className="fas fa-times-circle"></i>
											<span>Incorrect. The correct spelling is: <strong>{WORDS_LIST[currentWordIndex]}</strong></span>
										</>
									)}
								</div>
							)}

							{/* Action Buttons */}
							{showResult && (
								<div className="action-buttons">
									<button className="repractice-btn" onClick={handleRepractice}>
										<i className="fas fa-redo"></i> Re-practice This Word
									</button>
									<button className="next-btn" onClick={handleNextWord}>
										{currentWordIndex < WORDS_LIST.length - 1 ? (
											<><i className="fas fa-arrow-right"></i> Next Word</>
										) : (
											<><i className="fas fa-flag-checkered"></i> See Results</>
										)}
									</button>
								</div>
							)}
						</div>

						{/* Current Stats */}
						<div className="current-stats">
							<div className="stat correct-stat">
								<i className="fas fa-check"></i>
								<span>{correctCount} Correct</span>
							</div>
							<div className="stat incorrect-stat">
								<i className="fas fa-times"></i>
								<span>{incorrectCount} Incorrect</span>
							</div>
						</div>
					</div>
				)}

				{/* Results Stage */}
				{stage === "results" && (
					<div className="practice-results">
						<h1>🏆 Practice Complete!</h1>
						{nickname && <p className="results-name">Great effort, <strong>{nickname}</strong>!</p>}

						<div className="score-summary">
							<div className="score-circle">
								<span className="score-percentage">{percentage}%</span>
								<span className="score-label">Score</span>
							</div>
							<div className="score-details">
								<div className="detail-item correct">
									<i className="fas fa-check-circle"></i>
									<span>{correctCount} Correct</span>
								</div>
								<div className="detail-item incorrect">
									<i className="fas fa-times-circle"></i>
									<span>{incorrectCount} Incorrect</span>
								</div>
								<div className="detail-item total">
									<i className="fas fa-list"></i>
									<span>{results.length} Total Words</span>
								</div>
							</div>
						</div>

						{/* Performance Message */}
						<div className="performance-message">
							{percentage >= 90 && <p>🌟 Excellent! You're a spelling champion!</p>}
							{percentage >= 70 && percentage < 90 && <p>👏 Great job! Keep practicing to improve!</p>}
							{percentage >= 50 && percentage < 70 && <p>💪 Good effort! More practice will help!</p>}
							{percentage < 50 && <p>📚 Keep learning! Practice makes perfect!</p>}
						</div>

						{/* Incorrect Words Review */}
						{incorrectCount > 0 && (
							<div className="incorrect-review">
								<h3><i className="fas fa-book"></i> Words to Review:</h3>
								<div className="incorrect-words-list">
									{results.filter(r => !r.isCorrect).map((result, index) => (
										<div key={index} className="incorrect-word-item">
											<span className="your-answer">You wrote: <strong>{result.userAnswer}</strong></span>
											<span className="correct-answer">Correct: <strong>{result.word}</strong></span>
										</div>
									))}
								</div>
							</div>
						)}

						<div className="results-actions">
							<button className="restart-btn" onClick={handleRestart}>
								<i className="fas fa-redo-alt"></i> Practice Again
							</button>
							<Link to="/" className="home-btn">
								<i className="fas fa-home"></i> Back to Home
							</Link>
						</div>
					</div>
				)}
			</div>
		</section>
	);
}

export default Practice;
